import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes = [
  // ── Public (DefaultLayout) ────────────────────────────────────────────────
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      { path: '',                    name: 'home',          component: () => import('@/views/public/HomeView.vue') },
      { path: 'events',              name: 'events',        component: () => import('@/views/public/EventsView.vue') },
      { path: 'pricing',             name: 'pricing',       component: () => import('@/views/public/PricingView.vue') },
      { path: 'about',               name: 'about',         component: () => import('@/views/public/AboutView.vue') },
      { path: 'how-it-works',        name: 'how-it-works',  component: () => import('@/views/public/HowItWorksView.vue') },
      { path: 'faq',                 name: 'faq',           component: () => import('@/views/public/FaqView.vue') },
      { path: 'contact',             name: 'contact',       component: () => import('@/views/public/ContactView.vue') },
      { path: 'privacy',             name: 'privacy',       component: () => import('@/views/public/PrivacyView.vue') },
      { path: 'terms',               name: 'terms',         component: () => import('@/views/public/TermsView.vue') },
      { path: 'docs',                name: 'docs',          component: () => import('@/views/public/DocsView.vue') },
      { path: 'blog',                name: 'blog',          component: () => import('@/views/public/BlogView.vue') },
      { path: 'blog/:slug',          name: 'blog-post',     component: () => import('@/views/public/BlogPostView.vue') },
      { path: 'affiliate',           name: 'affiliate-program', component: () => import('@/views/public/AffiliateView.vue') },
      { path: 'affiliate/join',      name: 'affiliate-join', component: () => import('@/views/public/AffiliateJoinView.vue') },
    ],
  },

  // ── Event pages (EventLayout — minimal header only) ──────────────────────
  {
    path: '/events',
    component: () => import('@/layouts/EventLayout.vue'),
    children: [
      { path: ':slug',          name: 'event-landing',   component: () => import('@/views/public/EventLandingView.vue') },
      { path: ':slug/register', name: 'event-register',  component: () => import('@/views/public/RegisterForEventView.vue') },
    ],
  },

  // ── Auth (AuthLayout) — children use absolute paths so URLs stay /login etc.
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      { path: '/login',           name: 'login',           component: () => import('@/views/auth/LoginView.vue') },
      { path: '/register',        name: 'register',        component: () => import('@/views/auth/RegisterView.vue') },
      { path: '/forgot-password', name: 'forgot-password', component: () => import('@/views/auth/ForgotPasswordView.vue') },
      { path: '/reset-password',  name: 'reset-password',  component: () => import('@/views/auth/ResetPasswordView.vue') },
      { path: '/verify-email',    name: 'verify-email',    component: () => import('@/views/auth/EmailVerifyView.vue') },
    ],
  },

  // ── Super Admin ───────────────────────────────────────────────────────────
  {
    path: '/super-admin',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true, roles: ['super_admin'] },
    children: [
      { path: 'dashboard',     name: 'sa-dashboard',      component: () => import('@/views/superadmin/SADashboardView.vue') },
      { path: 'orgs',          name: 'sa-orgs',           component: () => import('@/views/superadmin/SAOrgsView.vue') },
      { path: 'orgs/:id',      name: 'sa-org-detail',     component: () => import('@/views/superadmin/SAOrgDetailView.vue') },
      { path: 'events-reports', name: 'sa-events-reports', component: () => import('@/views/superadmin/SAEventsReportsView.vue') },
      { path: 'plans',         name: 'sa-plans',          component: () => import('@/views/superadmin/SAPlansView.vue') },
      { path: 'subscriptions', name: 'sa-subscriptions',  component: () => import('@/views/superadmin/SASubscriptionsView.vue') },
      { path: 'users',         name: 'sa-users',          component: () => import('@/views/superadmin/SAUsersView.vue') },
      { path: 'content',       name: 'sa-content',        component: () => import('@/views/superadmin/SAContentView.vue') },
      { path: 'activity-log',  name: 'sa-activity-log',   component: () => import('@/views/superadmin/SAActivityLogView.vue') },
      { path: 'blog',          name: 'sa-blog',           component: () => import('@/views/superadmin/SABlogView.vue') },
      { path: 'faq',           name: 'sa-faq',            component: () => import('@/views/superadmin/SAFaqView.vue') },
      { path: 'settings',      name: 'sa-settings',       component: () => import('@/views/superadmin/SASettingsView.vue') },
      { path: 'privacy',            name: 'sa-privacy',           component: () => import('@/views/superadmin/SAPrivacyView.vue') },
      { path: 'terms',              name: 'sa-terms',             component: () => import('@/views/superadmin/SATermsView.vue') },
      { path: 'affiliates',         name: 'sa-affiliates',        component: () => import('@/views/superadmin/SAAffiliatesView.vue') },
      { path: 'affiliate-payouts',  name: 'sa-affiliate-payouts', component: () => import('@/views/superadmin/SAAffilliatePayoutsView.vue') },
      { path: 'broadcast',          name: 'sa-broadcast',         component: () => import('@/views/superadmin/SABroadcastView.vue') },
      { path: 'support',            name: 'sa-support',           component: () => import('@/views/superadmin/SASupportView.vue') },
      { path: 'profile',            name: 'sa-profile',           component: () => import('@/views/shared/MyProfileView.vue') },
    ],
  },

  // ── Org Admin ─────────────────────────────────────────────────────────────
  {
    path: '/admin',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true, roles: ['org_admin', 'event_manager'] },
    children: [
      { path: 'dashboard',       name: 'oa-dashboard',    component: () => import('@/views/orgadmin/OADashboardView.vue') },
      { path: 'events',          name: 'oa-events',       component: () => import('@/views/orgadmin/OAEventsView.vue') },
      { path: 'events/create',   name: 'oa-event-create', component: () => import('@/views/orgadmin/OAEventCreateView.vue'), meta: { roles: ['org_admin'] } },
      { path: 'events/:id/edit', name: 'oa-event-edit',   component: () => import('@/views/orgadmin/OAEventEditView.vue') },
      { path: 'events/:id',      name: 'oa-event-detail', component: () => import('@/views/orgadmin/OAEventDetailView.vue') },
      { path: 'attendees',       name: 'oa-attendees',    component: () => import('@/views/orgadmin/OAAttendeesView.vue') },
      { path: 'payments',        name: 'oa-payments',     component: () => import('@/views/orgadmin/OAPaymentsView.vue') },
      { path: 'financial-report', name: 'oa-financial-report', component: () => import('@/views/orgadmin/OAFinancialReportView.vue') },
      { path: 'certificates',    name: 'oa-certificates', component: () => import('@/views/orgadmin/OACertificatesView.vue') },
      { path: 'presentation-certificates', name: 'oa-presentation-certificates', component: () => import('@/views/orgadmin/OAPresentationCertificatesView.vue') },
      { path: 'staff',           name: 'oa-staff',        component: () => import('@/views/orgadmin/OAStaffView.vue'), meta: { roles: ['org_admin'] } },
      { path: 'materials',       name: 'oa-materials',      component: () => import('@/views/orgadmin/OAMaterialsReportView.vue') },
      { path: 'coupons',         name: 'oa-coupons',        component: () => import('@/views/orgadmin/OACouponsView.vue') },
      { path: 'subscription',    name: 'oa-subscription',   component: () => import('@/views/orgadmin/OASubscriptionView.vue'), meta: { roles: ['org_admin'] } },
      { path: 'support',         name: 'oa-support',        component: () => import('@/views/orgadmin/OASupportView.vue') },
      { path: 'activity-log',    name: 'oa-activity-log',   component: () => import('@/views/orgadmin/OAActivityLogView.vue') },
      { path: 'settings',        name: 'oa-settings',       component: () => import('@/views/orgadmin/OASettingsView.vue'), meta: { roles: ['org_admin'] } },
      { path: 'profile',         name: 'oa-profile',        component: () => import('@/views/shared/MyProfileView.vue') },
    ],
  },

  // ── Staff ─────────────────────────────────────────────────────────────────
  {
    path: '/staff',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true, roles: ['staff'] },
    children: [
      { path: 'dashboard',          name: 'staff-dashboard',          component: () => import('@/views/staff/StaffDashboardView.vue') },
      { path: 'checkin',            name: 'staff-checkin',            component: () => import('@/views/staff/StaffCheckinView.vue') },
      { path: 'materials',          name: 'staff-materials',          component: () => import('@/views/staff/StaffMaterialsView.vue') },
      { path: 'materials-report',   name: 'staff-materials-report',   component: () => import('@/views/staff/StaffMaterialsReportView.vue') },
      { path: 'profile',            name: 'staff-profile',            component: () => import('@/views/shared/MyProfileView.vue') },
    ],
  },

  // ── Attendee ──────────────────────────────────────────────────────────────
  {
    path: '/attendee',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true, roles: ['attendee'] },
    children: [
      { path: 'dashboard',    name: 'att-dashboard',    component: () => import('@/views/attendee/AttendeeDashboardView.vue') },
      { path: 'tickets',      name: 'att-tickets',      component: () => import('@/views/attendee/AttendeeTicketsView.vue') },
      { path: 'receipts',     name: 'att-receipts',     component: () => import('@/views/attendee/AttendeeReceiptsView.vue') },
      { path: 'certificates', name: 'att-certificates', component: () => import('@/views/attendee/AttendeeCertificatesView.vue') },
      { path: 'profile',      name: 'att-profile',      component: () => import('@/views/attendee/AttendeeProfileView.vue') },
    ],
  },

  // ── Affiliate ─────────────────────────────────────────────────────────────
  {
    path: '/affiliate',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true, roles: ['affiliate'] },
    children: [
      { path: 'dashboard',  name: 'aff-dashboard',  component: () => import('@/views/affiliate/AffiliateDashboardView.vue') },
      { path: 'referrals',  name: 'aff-referrals',  component: () => import('@/views/affiliate/AffiliateReferralsView.vue') },
      { path: 'payouts',    name: 'aff-payouts',    component: () => import('@/views/affiliate/AffiliatePayoutsView.vue') },
      { path: 'profile',    name: 'aff-profile',    component: () => import('@/views/shared/MyProfileView.vue') },
    ],
  },

  // ── Payment callbacks ─────────────────────────────────────────────────────
  {
    path: '/payment/verify',
    name: 'payment-verify',
    component: () => import('@/views/public/PaymentVerifyView.vue'),
  },
  {
    path: '/subscription/verify',
    name: 'subscription-verify',
    component: () => import('@/views/public/SubscriptionVerifyView.vue'),
  },

  // ── Catch-all ─────────────────────────────────────────────────────────────
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue') },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach((to) => {
  const auth = useAuthStore()

  // Token exists but user/role is missing — clear stale auth and continue
  if (auth.token && !auth.role) {
    auth.logout()
  }

  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.roles && !to.meta.roles.includes(auth.role)) {
    if (!auth.isLoggedIn) return { name: 'login', query: { redirect: to.fullPath } }
    const dest = auth.dashboardRoute
    return dest !== '/' ? { path: dest } : { name: 'login' }
  }

  // Redirect already-logged-in users away from auth pages only
  if (auth.isLoggedIn && (to.name === 'login' || to.name === 'register')) {
    const dest = auth.dashboardRoute
    if (dest !== '/') return { path: dest }
  }
})

// After a redeploy, tabs left open reference old chunk hashes; lazy imports then
// fail silently and links stop working. Hard-reload to the target URL instead.
router.onError((error, to) => {
  const msg = String(error?.message || '')
  if (
    error?.name === 'ChunkLoadError' ||
    /loading (css )?chunk .* failed/i.test(msg) ||
    msg.includes('Failed to fetch dynamically imported module')
  ) {
    window.location.assign(to?.fullPath || window.location.href)
  }
})

export default router
